import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Reddit Ads pixel for the platform app. The pixel ID is public by design
// (it identifies the ad account, it grants no access) and is hardcoded like
// the PostHog key in index.tsx, since the production Docker build injects no
// env vars. Leave empty to disable the pixel entirely.
const REDDIT_PIXEL_ID = 'a2_je7od8y72pql';

type Rdt = {
  (...args: unknown[]): void;
  sendEvent?: (...args: unknown[]) => void;
  callQueue?: unknown[];
};

declare global {
  interface Window {
    rdt?: Rdt;
  }
}

/** Loads the Reddit pixel script and initializes it. No-op when no pixel ID is configured. */
export function initRedditPixel() {
  if (!REDDIT_PIXEL_ID || typeof window === 'undefined' || window.rdt) return;

  // Official Reddit pixel bootstrap: queue calls until pixel.js loads.
  const rdt: Rdt = function (...args: unknown[]) {
    if (rdt.sendEvent) {
      rdt.sendEvent.apply(rdt, args);
    } else {
      rdt.callQueue!.push(args);
    }
  };
  rdt.callQueue = [];
  window.rdt = rdt;

  const script = document.createElement('script');
  script.src = `https://www.redditstatic.com/ads/pixel.js?pixel_id=${REDDIT_PIXEL_ID}`;
  script.async = true;
  document.head.appendChild(script);

  window.rdt('init', REDDIT_PIXEL_ID);
}

/** Fire a Reddit event. No-op when the pixel is not configured. */
export function trackRedditEvent(event: string, metadata?: Record<string, unknown>) {
  if (!REDDIT_PIXEL_ID || typeof window === 'undefined' || !window.rdt) return;
  window.rdt('track', event, metadata);
}

/**
 * Fires a Reddit PageVisit on every route change (react-router doesn't reload
 * the page, so the pixel's automatic on-load PageVisit only covers the first
 * hit). Mount once inside the Router.
 */
export function RedditPixelRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    trackRedditEvent('PageVisit');
  }, [location.pathname]);

  return null;
}
